import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";

import { FormStep, MultistepForm } from "stentor-models";

import { FormActionResponseData, FormNextData, FormStepUpdateDisplay } from ".";
import { FormStepDisplay, isFormStepExternalWidget } from "../FormStepDisplay";
import { FormFrame } from "../FormFrame";
import { ExternalWidgetConfig } from "../../utils/useExternalWidget";
import { SearchWidgetConfigContext } from "../../utils/SearchWidgetConfigContext";

import { SimpleNavigationHeader } from "../SimpleNavigationHeader";
import { err } from "../../utils";
import { addContextPrefix } from "../../utils/comparator";
import { isInServiceArea } from "../../utils/isInServiceArea";
import { extractZipFromAddress } from "../../utils/extractZipFromAddress";

import styles from "./FormDisplay.module.scss";

interface FormNextResponse {
    displays?: FormStepUpdateDisplay[];
}

/**
 * Shown on the step whose FORM_SUBMIT dispatch failed. That dispatch is the request
 * that creates the lead, so a silent failure loses it -- the user must be told rather
 * than left believing they submitted (issue #1373).
 */
export const SUBMIT_ERROR_MESSAGE =
    "We couldn't submit your information. Please check your connection and try again.";

/**
 * How long the handoff step waits for the FORM_SUBMIT response before injecting the partner
 * script with only the static config.
 *
 * The handoff is reached by a submit, and FormWidget dispatches FORM_SUBMIT fire-and-forget so the
 * UI is not blocked -- the step goes active before the response lands. The per-submission values
 * (zipCode, name, trade) only exist in that response, and a partner script that reads its config
 * global once at load will never see them if we inject first. CostGuide does exactly that: it read
 * the static config, found no zip, and asked the visitor for one we already had.
 *
 * Waiting is bounded so a failed or slow submit still reaches the partner's form. That is degraded
 * -- no prefill -- rather than broken.
 */
export const EXTERNAL_WIDGET_CONFIG_WAIT_MS = 5000;

interface StepOverride {
    fields?: unknown[];
    externalWidget?: {
        config?: Record<string, string | number | boolean>;
    };
}

/**
 * Extended FormStep interface to support additional per-step properties not yet in stentor-models.
 */
export interface ExtendedFormStep extends FormStep {
    /**
     * If true, shows a browser warning dialog when the user attempts to leave the page.
     * Useful for confirmation steps where the user hasn't submitted yet.
     */
    warnBeforeUnload?: boolean;
    /**
     * Optional message to display in a visual warning banner.
     * The browser's beforeunload dialog will show a generic message (browsers ignore custom messages for security).
     * This message is displayed in a yellow warning banner for better visibility, especially on mobile.
     */
    warnBeforeUnloadMessage?: string;
    /**
     * When true, fires a FORM_NEXT server callback before advancing past this step.
     * The server can return a FORM_STEP_UPDATE display to dynamically replace the next step's fields.
     * If the call fails or exceeds 3 seconds, the widget proceeds with the existing client-side fields.
     */
    serverCallback?: boolean;
}

interface StandardFormData {
    address?: string;
    email?: string;
    full_name?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    zip?: string;
    zip_code?: string;

    [key: string]: string | undefined;
}

export interface FormDisplayProps {
    readonly form: MultistepForm;
    readonly loading: boolean;
    /**
     * Zero-based step to open on, instead of the first.
     *
     * For previews that want to show a particular step -- the booking preview on
     * get.xapp.ai opens on the date and time step, because the calendar is the
     * part worth showing. Applied once, when the form first arrives with steps,
     * so navigating away from it sticks. An index the form has no step for is
     * ignored and the form opens where it always would.
     */
    readonly initialStep?: number;
    /**
     * Open the date/time step on the soonest day that has times, rather than on
     * "Select a date to see the available times."
     *
     * For a preview that lands straight on that step: an empty column beside a
     * calendar reads as a form with nothing to offer. Off by default -- on a real
     * booking page a preselected date is one the customer never chose.
     */
    readonly preselectFirstAvailableDate?: boolean;
    /**
     * Fired with the step index when {@link FormDisplayProps.initialStep} is
     * actually applied, and not at all when it is ignored.
     *
     * FormWidget keeps its own `currentStepRef` and only ever writes it from the
     * next/previous tracking callbacks, so a form that jumps straight to a step
     * without saying so leaves that ref on 0 -- and every abandonment path (close,
     * the beforeunload beacon, the tab-hidden timeout) reads it for lastStepIndex
     * and completionPercentage. This keeps the two in step.
     */
    onTrackInitialStep?: (stepIndex: number) => void;
    /**
     * Dispatches the form action. For FORM_SUBMIT this is the request that creates the
     * lead, so the returned promise is awaited and a rejection is surfaced to the user
     * -- see onSubmitForm. Implementations that return void keep the old fire-and-forget
     * behaviour.
     */
    onActionResponse(data: unknown, action: string): void | Promise<unknown>;
    isShowCase?: boolean;
    /**
     * Callback to track next_step event
     */
    onTrackNextStep?: (
        stepName: string,
        stepIndex: number,
        stepData: Record<string, unknown>,
        isLastStep: boolean
    ) => void;
    /**
     * Callback to track previous_step event
     */
    onTrackPreviousStep?: (
        fromStepName: string,
        fromStepIndex: number,
        toStepName: string,
        toStepIndex: number
    ) => void;
    /**
     * Callback to track submit event
     */
    onTrackSubmit?: (
        stepName: string,
        stepIndex: number,
        completedData: Record<string, unknown>,
        totalSteps: number
    ) => void;
    /**
     * Callback to track a FORM_SUBMIT dispatch that failed outright. onTrackSubmit fires
     * on a different endpoint before the dispatch, so without this a lost lead leaves a
     * submit event behind and no counter-signal at all.
     */
    onTrackSubmitFailure?: (
        stepName: string,
        stepIndex: number,
        totalSteps: number,
        reason: string
    ) => void;
    /**
     * Ref to sync current step data for tracking purposes
     */
    currentStepDataRef?: React.MutableRefObject<Record<string, unknown>>;
    /**
     * When provided, fires a FORM_NEXT request before advancing to the next step.
     * The server can return a FORM_STEP_UPDATE display to replace the next step's fields.
     * If the call fails or exceeds 3 seconds, the widget proceeds with the existing fields.
     */
    onFormNext?: (data: FormNextData) => Promise<unknown>;
    /**
     * A FORM_STEP_UPDATE display extracted from the (fire-and-forget) FORM_SUBMIT
     * response, forwarded down from FormWidget. A new object reference triggers
     * applying the update to the named step's overrides -- see the effect below.
     */
    stepUpdate?: FormStepUpdateDisplay;
    /**
     * Callback to track a booking-widget handoff step's anchor gaining content
     * within its render timeout
     */
    onTrackHandoffRendered?: (stepName: string, provider: string) => void;
    /**
     * Callback to track a booking-widget handoff step's render timeout elapsing
     * with an empty anchor
     */
    onTrackHandoffNoRender?: (stepName: string, provider: string) => void;
    /**
     * Callback to track the partner script invoking the configured success callback
     */
    onTrackHandoffScheduled?: (stepName: string, provider: string, payload?: unknown) => void;
    /**
     * Callback to track a booking-widget handoff step's script failing to load
     */
    onTrackHandoffError?: (stepName: string, provider: string) => void;
}

export const FormDisplay: FC<FormDisplayProps> = (props) => {
    const {
        form,
        onActionResponse,
        isShowCase,
        loading,
        onTrackNextStep,
        onTrackPreviousStep,
        onTrackSubmit,
        onTrackSubmitFailure,
        currentStepDataRef,
        initialStep,
        preselectFirstAvailableDate,
        onTrackInitialStep,
        onFormNext,
        stepUpdate,
        onTrackHandoffRendered,
        onTrackHandoffNoRender,
        onTrackHandoffScheduled,
        onTrackHandoffError,
    } = props;
    // TODO: const title = form?.title;

    // Partner chrome from a resolved style preset. Undefined for an embedded form --
    // presets only ever resolve on the stand-alone page -- so the markup below is
    // byte for byte what it was before presets existed.
    const { framing } = useContext(SearchWidgetConfigContext);

    const [currentStep, setCurrentStep] = useState(0);
    /** Whether {@link FormDisplayProps.initialStep} has been honoured yet. */
    const appliedInitialStep = useRef(false);
    const [stepOverrides, setStepOverrides] = useState<Record<string, StepOverride>>({});
    // The index of the step whose submit failed, so the message renders on that step
    // only -- an error shown on some *other* step would be nonsense.
    //
    // In practice this is visible for the final step, which is the one that matters:
    // it never advances, so it is still on screen when its dispatch rejects. A
    // non-final submitting step has already advanced by then and inactive steps are
    // display:none, so its banner stays hidden unless the user navigates back to it.
    // That is deliberate: its submit is not the lead (the final one carries all of
    // formData), and blocking the advance would strand the user short of the step that
    // does create the lead. onTrackSubmitFailure still fires for it either way.
    const [failedSubmitStep, setFailedSubmitStep] = useState<number | undefined>(undefined);
    // const [submitted, setSubmitted] = useState(false);

    // Applies a FORM_STEP_UPDATE display (from either a FORM_NEXT or FORM_SUBMIT
    // response) to the named step's overrides. externalWidget.config is
    // shallow-merged over whatever config is already present (the static step
    // definition, or an earlier update), with the new values winning on conflict.
    // A fields-only update behaves exactly as before: it fully replaces the
    // override's fields, leaving any existing externalWidget override untouched.
    const applyFormStepUpdate = (display: FormStepUpdateDisplay) => {
        const stepName = display.step;
        if (!stepName) {
            return;
        }
        setStepOverrides((prev) => {
            const existing = prev[stepName];
            const next: StepOverride = { fields: display.fields || [] };
            if (display.externalWidget?.config) {
                handoffConfigSettledRef.current[stepName] = true;
                next.externalWidget = {
                    config: {
                        ...existing?.externalWidget?.config,
                        ...display.externalWidget.config,
                    },
                };
            } else if (existing?.externalWidget) {
                next.externalWidget = existing.externalWidget;
            }
            return { ...prev, [stepName]: next };
        });
    };

    // Applies a FORM_STEP_UPDATE surfaced from the fire-and-forget FORM_SUBMIT
    // response. `stepUpdate` is a fresh object reference each time FormWidget
    // detects one, so this only ever runs on a genuine new update.
    useEffect(() => {
        if (stepUpdate) {
            applyFormStepUpdate(stepUpdate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepUpdate]);

    // True from the moment a submit starts advancing to a handoff step until that submit settles.
    // See EXTERNAL_WIDGET_CONFIG_WAIT_MS.
    const [awaitingHandoffConfig, setAwaitingHandoffConfig] = useState(false);
    // The wait is a once-per-handoff-step thing, keyed by step name. Once per FORM would be
    // wrong twice over: a visitor who steps back and returns would re-arm the wait and sit in
    // front of a spinner over an embed that already rendered, and a form with a SECOND handoff
    // step would inherit the first's settle, never arm its own wait, and hand that partner the
    // static config on the render it became active.
    const handoffConfigSettledRef = useRef<Record<string, boolean>>({});

    // Bounds the wait above. Reset per step so returning to a handoff waits again rather than
    // inheriting a previous step's expiry.
    const [externalWidgetWaitElapsed, setExternalWidgetWaitElapsed] = useState(false);
    useEffect(() => {
        setExternalWidgetWaitElapsed(false);
        const activeStep = form.steps?.[currentStep];
        if (!activeStep || !isFormStepExternalWidget(activeStep)) {
            return undefined;
        }
        const timer = setTimeout(() => {
            setExternalWidgetWaitElapsed(true);
            // Given up on for good, for THIS step, not just for this pass.
            handoffConfigSettledRef.current[activeStep.name] = true;
            setAwaitingHandoffConfig(false);
        }, EXTERNAL_WIDGET_CONFIG_WAIT_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);

    const fieldTests = useRef({});

    useEffect(() => {
        if (Array.isArray(form.steps) && form.steps.length > 0) {
            for (const step of form.steps) {
                if (step.condition) {
                    const conditionWithThis = addContextPrefix(step.condition, "context", [
                        "isInServiceArea",
                    ]);

                    fieldTests.current[step.name] = (context) =>
                        // eslint-disable-next-line no-new-func
                        new Function(
                            "context",
                            "isInServiceArea",
                            `return (${conditionWithThis});`
                        )(context, isInServiceArea);
                }
            }
        }
    }, [form?.steps]);

    useEffect(() => {
        // Check if currentIndex is out of bounds
        if (currentStep < 0 || currentStep >= form?.steps?.length) {
            setCurrentStep(0);
        }
    }, [form?.steps, currentStep]);

    useEffect(() => {
        // Deliberately not a useState initialiser. The form arrives
        // asynchronously, so at mount there are no steps yet and the bounds
        // guard above would reset the index to 0 before it ever rendered.
        //
        // The ref makes it fire once. Without it, every re-render carrying the
        // same initialStep would drag the user back to the step they had just
        // navigated away from.
        if (appliedInitialStep.current) return;
        if (typeof initialStep !== "number") return;

        const steps = form?.steps;
        if (!steps?.length) return;

        appliedInitialStep.current = true;

        // An index the form has no step for is the caller's mistake. Opening
        // where the form always would beats rendering nothing.
        if (initialStep > 0 && initialStep < steps.length) {
            setCurrentStep(initialStep);
            onTrackInitialStep?.(initialStep);
        }
    }, [form?.steps, initialStep, onTrackInitialStep]);

    // Memoize the current step's warnBeforeUnload value to prevent unnecessary effect re-runs
    const shouldWarnBeforeUnload = useMemo(() => {
        const currentFormStep = form.steps?.[currentStep] as ExtendedFormStep | undefined;
        return currentFormStep?.warnBeforeUnload ?? false;
    }, [currentStep, form.steps]);

    // Handle beforeunload warning for steps that require it
    useEffect(() => {
        if (shouldWarnBeforeUnload) {
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                e.preventDefault();
                // eslint-disable-next-line no-param-reassign
                e.returnValue = ""; // Required for Chrome
                return ""; // For older browsers
            };

            window.addEventListener("beforeunload", handleBeforeUnload);

            return () => {
                window.removeEventListener("beforeunload", handleBeforeUnload);
            };
        }

        return undefined;
    }, [shouldWarnBeforeUnload]);

    const formData = useRef<StandardFormData>({});
    const formDataWithLabels = useRef({});
    const lastStepData = useRef<object>({});
    const formSubmittedRef = useRef(false);

    // Re-arm the submit guard whenever the form moves to a different step, so the
    // guard only ever collapses repeat submissions of the *same* step. The final
    // step never advances, so its latch holds and the lead is still submitted once.
    useEffect(() => {
        formSubmittedRef.current = false;
    }, [currentStep]);

    const nextStep = async (submit?: boolean) => {
        let nextStepIndex = -1;

        for (let i = currentStep + 1; i < form.steps.length; i++) {
            const formStepItem = form.steps[i];

            const test = fieldTests.current[formStepItem.name];

            const zip =
                formData.current.zip_code ||
                formData.current.zip ||
                extractZipFromAddress(formData.current.address);

            // Build the context to pass in
            const context = {
                ...formData.current,
                zip,
            };

            if (test) {
                // eslint-disable-next-line @typescript-eslint/ban-types
                if ((test as Function)(context)) {
                    nextStepIndex = i;
                    break;
                }
            } else {
                nextStepIndex = i;
                break;
            }
        }

        // Fire FORM_NEXT before advancing only when the current step has serverCallback: true
        const currentStepDef = form.steps[currentStep] as ExtendedFormStep;
        if (nextStepIndex >= 0 && !submit && onFormNext && currentStepDef?.serverCallback) {
            // Clear any stale override for the upcoming step before the server call,
            // so a timeout/error doesn't leave outdated fields from a previous pass.
            setStepOverrides((prev) => {
                const next = { ...prev };
                delete next[form.steps[nextStepIndex].name];
                return next;
            });
            try {
                const timeout = new Promise<undefined>((resolve) =>
                    setTimeout(() => resolve(undefined), 3000)
                );
                const request = onFormNext({
                    result: lastStepData.current as Record<string, unknown>,
                    form: form.name,
                    step: form.steps[currentStep].name,
                    nextStep: form.steps[nextStepIndex].name,
                });
                const response = await Promise.race([request, timeout]);
                const r = response as FormNextResponse;
                if (r?.displays) {
                    for (const display of r.displays) {
                        if (display.type === "FORM_STEP_UPDATE") {
                            applyFormStepUpdate(display);
                        }
                    }
                }
            } catch {
                // Graceful degradation: proceed with existing step fields
            }
        }

        // The handoff step is reached by a submit, and its per-submission config (zipCode, name,
        // trade) only exists in that response. Flag the wait BEFORE advancing so the step never
        // renders unflagged for a frame and injects the partner script with the static config.
        const advancingToHandoff =
            nextStepIndex >= 0 && !!submit && isFormStepExternalWidget(form.steps[nextStepIndex]);
        if (advancingToHandoff && !handoffConfigSettledRef.current[form.steps[nextStepIndex].name]) {
            setAwaitingHandoffConfig(true);
        }

        if (nextStepIndex >= 0) {
            setCurrentStep(nextStepIndex);
        }

        if (!!submit) {
            // Awaited so FormStepDisplay's own isSubmitting state stays set for the
            // duration of the dispatch, and so a rejection is handled rather than
            // becoming an unhandled rejection (issue #1373).
            // Pass the current step index since state updates are async
            //
            // The wait is deliberately NOT released here. The dispatch resolving is not the
            // config arriving: FormWidget surfaces the FORM_STEP_UPDATE as state, which reaches
            // this component a render later. Releasing on settle injected the partner script
            // inside that gap with the static config only -- 7-14ms measured against the live
            // page, against CostGuide's own bundle landing ~90ms after, so whether a visitor was
            // prefilled came down to which of the two won. It is released by the override
            // actually being present, and bounded by EXTERNAL_WIDGET_CONFIG_WAIT_MS so a submit
            // that fails or returns nothing still reaches the partner's form without prefill.
            await onSubmitForm(currentStep);
        }
    };

    const prevStep = () => {
        const fromStepIndex = currentStep;
        const fromStepName = form.steps?.[fromStepIndex]?.name || "unknown";

        for (let i = currentStep - 1; i >= 0; i--) {
            const step = form.steps[i];
            const test = fieldTests.current[step.name];

            const zip =
                formData.current.zip_code ||
                formData.current.zip ||
                extractZipFromAddress(formData.current.address);

            const context = {
                ...formData.current,
                zip,
            };

            if (test) {
                // eslint-disable-next-line @typescript-eslint/ban-types
                if ((test as Function)(context)) {
                    // Track previous_step event before changing step
                    if (onTrackPreviousStep) {
                        onTrackPreviousStep(fromStepName, fromStepIndex, step.name, i);
                    }
                    setCurrentStep(i);
                    break;
                }
            } else {
                // Track previous_step event before changing step
                if (onTrackPreviousStep) {
                    onTrackPreviousStep(fromStepName, fromStepIndex, step.name, i);
                }
                setCurrentStep(i);
                break;
            }
        }
    };

    const handleData = (data: object) => {
        lastStepData.current = data;
        formData.current = { ...formData.current, ...data };
    };

    const getData = () => {
        return formData.current;
    };

    const handleDataWithLabels = (data: object) => {
        formDataWithLabels.current = { ...formDataWithLabels.current, ...data };
    };

    const getDataWithLabels = () => {
        return formDataWithLabels.current;
    };

    const onSubmitForm = async (submitStepIndex?: number) => {
        // Guard against double-firing the backend submission for a single step: a
        // double-click before the "submitting" UI state flushes, a re-render, or a
        // retry can invoke this more than once for the same submission. The latch is
        // scoped to the current step, re-armed by the effect above -- see there for why.
        if (formSubmittedRef.current) {
            return;
        }
        formSubmittedRef.current = true;

        // Use provided step index or fall back to currentStep
        const stepIndex = submitStepIndex ?? currentStep;
        const safeStepIndex = stepIndex >= 0 && stepIndex < form.steps.length ? stepIndex : 0;

        // Track submit event if tracking callback is provided
        if (onTrackSubmit) {
            onTrackSubmit(
                form.steps[safeStepIndex].name,
                safeStepIndex,
                formData.current as Record<string, unknown>,
                form.steps.length
            );
        }

        const data: FormActionResponseData = {
            result: formData.current,
            form: form.name,
            step: form.steps[safeStepIndex].name,
            final: !!form.steps[safeStepIndex].final,
        };
        if (typeof onActionResponse !== "function") {
            return;
        }

        // This dispatch is what creates the lead. onTrackSubmit above has already
        // recorded the submit on a *different* endpoint, so if this fails and nobody
        // notices, the analytics say the form was submitted and no lead exists --
        // exactly the loss reported in issue #1373. Await it, and on failure re-arm the
        // guard (otherwise the user's retry click is swallowed), tell the user, and
        // emit the counter-signal.
        setFailedSubmitStep(undefined);
        try {
            await onActionResponse(data, "FORM_SUBMIT");
        } catch (error) {
            formSubmittedRef.current = false;
            setFailedSubmitStep(safeStepIndex);

            const reason = error instanceof Error ? error.message : String(error);
            err(`FORM_SUBMIT failed on step ${form.steps[safeStepIndex].name}: ${reason}`);

            if (onTrackSubmitFailure) {
                onTrackSubmitFailure(
                    form.steps[safeStepIndex].name,
                    safeStepIndex,
                    form.steps.length,
                    reason
                );
            }
        }
    };

    const onHeaderClick = (step: string) => {
        const condition = (formStep: FormStep) => formStep.name === step;
        const index = form.steps.findIndex(condition);

        if (index === -1) {
            err(`Header click: Cannot find step to jump to: ${step}`);
            return;
        }

        setCurrentStep(index);
    };

    // A booking-widget handoff step is terminal (previousAction/nextAction: "omit"),
    // so the only way out of it is a forced jump: the partner script failed to
    // render or errored, and externalWidget.fallbackStep names where to go instead.
    const jumpToExternalWidgetFallback = (fallbackStep: string) => {
        const index = form.steps.findIndex((formStep) => formStep.name === fallbackStep);

        if (index === -1) {
            err(`External widget fallback: Cannot find step to jump to: ${fallbackStep}`);
            return;
        }

        setCurrentStep(index);
    };

    let step = currentStep;

    // Safety
    if (currentStep < 0 || currentStep >= form?.steps?.length) {
        step = 0;
    }

    // In labelHeader mode SimpleNavigationHeader renders the current step's header label as a
    // full-width h4 banner. When a partner preset draws its own tab that is two headers stacked on
    // one step -- and the banner is the one nobody designed for this layout: the labels come from
    // the handler's stepper ("Request", "Contact Info"), sized for a progress strip. The tab wins.
    //
    // Only the label variant. The stepper variant is genuine progress UI and stays.
    //
    // And only where a tab is actually drawn. The tab is per-step and only on FRAMED steps -- the
    // handoff step is deliberately unframed, since the partner's embed brings its own chrome -- so
    // suppressing form-wide would leave that step identifying itself with neither banner nor tab.
    const activeStepHandsOff = isFormStepExternalWidget(form.steps[step]);
    const activeStepFramed = !activeStepHandsOff;
    const partnerTabReplacesLabelHeader =
        !!framing?.tabLabel && !!form.labelHeader && activeStepFramed;

    // The handoff step wears the partner's chrome and none of ours. `form.header` only names
    // OUR steps, so there is no entry for this one and the label header rendered
    // `.empty_step_header`: a 42px bar in the header colour with nothing written in it, sitting
    // directly above the partner's own card. The tab is already off here (the step is unframed),
    // so suppressing the banner leaves the step with no heading of ours at all, which is the
    // point -- their embed introduces itself.
    // Not gated on `framing`. It was, and then the CostGuide preset dropped its framing, which
    // silently switched this whole behaviour back off -- the empty banner and our border returned
    // to the handoff step with every test still green. A handoff step wears the partner's chrome
    // whether or not a preset is drawing any of ours.
    const bareHandoff = activeStepHandsOff;
    const hideLabelHeader = partnerTabReplacesLabelHeader || bareHandoff;

    return (
        // The marker, not a class: .xapp_search_response_part reserves space for the header with
        // a hand-written margin, and that margin cannot see whether the header was rendered.
        // FormDisplay is what decides, so it says so here and the ancestor collapses off it.
        //
        // `data-xapp-bare-handoff` is read the same way, by the widget root, to drop its border,
        // radius and shadow for this step -- our box around their box reads as a mistake.
        <div
            {...(hideLabelHeader ? { "data-xapp-header-suppressed": "" } : {})}
            {...(bareHandoff ? { "data-xapp-bare-handoff": "" } : {})}
        >
            {form.header.length > 0 && !hideLabelHeader && (
                <div
                    className={
                        !!isShowCase
                            ? styles.xapp_header_container_showcase
                            : styles.xapp_header_container
                    }
                >
                    <SimpleNavigationHeader
                        items={form.header}
                        onHeaderClick={onHeaderClick}
                        labelHeader={form.labelHeader}
                        stepName={form.steps[step].name}
                        isShowCase={isShowCase}
                    />
                </div>
            )}
            <div>
                {form.steps.map((formStep, index) => {
                    const override = stepOverrides[formStep.name];
                    let effectiveStep = formStep;
                    if (override?.fields) {
                        effectiveStep = {
                            ...effectiveStep,
                            fields: override.fields as typeof formStep.fields,
                        };
                    }
                    if (override?.externalWidget?.config) {
                        const staticExternalWidget = (
                            formStep as unknown as { externalWidget?: ExternalWidgetConfig }
                        ).externalWidget;
                        if (staticExternalWidget) {
                            effectiveStep = {
                                ...effectiveStep,
                                externalWidget: {
                                    ...staticExternalWidget,
                                    config: {
                                        ...staticExternalWidget.config,
                                        ...override.externalWidget.config,
                                    },
                                },
                            } as FormStep;
                        }
                    }
                    const isActive = form.steps[step].name === formStep.name;
                    // The frame goes around the steps we render ourselves. A booking
                    // handoff step is the partner's own embed and brings its own
                    // frame, so it renders unframed. That is a property of the step,
                    // not a piece of state, so crossing into the handoff turns the
                    // frame off without remounting anything.
                    const framed = !isFormStepExternalWidget(effectiveStep);
                    const stepDisplay = (
                        <FormStepDisplay
                            formStep={effectiveStep}
                            preselectFirstAvailableDate={preselectFirstAvailableDate}
                            onData={handleData}
                            onDataWithLabels={handleDataWithLabels}
                            onNextStep={step < form.steps.length ? nextStep : undefined}
                            onPrevStep={step > 0 ? prevStep : undefined}
                            getData={getData}
                            getDataWithLabels={getDataWithLabels}
                            loading={loading}
                            currentStepIndex={index}
                            totalSteps={form.steps.length}
                            onTrackNextStep={onTrackNextStep}
                            currentStepDataRef={currentStepDataRef}
                            isActive={isActive}
                            // Hold the partner script until the submit's values are merged in.
                            externalWidgetConfigPending={
                                awaitingHandoffConfig &&
                                isFormStepExternalWidget(formStep) &&
                                !override?.externalWidget?.config &&
                                !externalWidgetWaitElapsed
                            }
                            onTrackHandoffRendered={onTrackHandoffRendered}
                            onTrackHandoffNoRender={onTrackHandoffNoRender}
                            onTrackHandoffScheduled={onTrackHandoffScheduled}
                            onTrackHandoffError={onTrackHandoffError}
                            onExternalWidgetFallback={jumpToExternalWidgetFallback}
                            // Only when the frame is actually drawing an intro of its own.
                            // The frame no longer borrows the step's title, so hiding the
                            // step's intro unconditionally would drop the heading entirely.
                            // Suppressed when the frame draws its own intro, and always on a
                            // partner-embed step: the embed opens with its own heading, so ours
                            // is a second one for the same thing. #1659 narrowed this from "any
                            // framed form" and switched the handoff step's title back on.
                            hideIntro={
                                !!framing?.introTitle ||
                                !!framing?.introSubtitle ||
                                isFormStepExternalWidget(effectiveStep)
                            }
                            submitError={
                                failedSubmitStep === index ? SUBMIT_ERROR_MESSAGE : undefined
                            }
                        />
                    );
                    return (
                        <div
                            key={index}
                            className={`${styles.xapp_formstep} ${isActive ? styles.active : ""}`}
                            data-xapp-form-step-active={isActive ? "true" : "false"}
                        >
                            {framing ? (
                                <FormFrame framing={framing} framed={framed}>
                                    {stepDisplay}
                                </FormFrame>
                            ) : (
                                stepDisplay
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
